import { Link } from "react-router-dom";
import { ArrowRight, Package, Lightbulb, PenLine, Clapperboard } from "lucide-react";
import { Card, PageHeader, ProgressBar, StatusBadge, BtnLink, SaveNotice } from "@/components/kit";
import { MISSIONS, ANGLES } from "@/modules/premiere-video/content";
import { useProgress } from "@/modules/premiere-video/store";
import { doneCount, missionStatus, resumeMission, assembleScript, filled, filmedCount, prepCount, stepsCount } from "@/modules/premiere-video/logic";

function ProgressBlock() {
  const { state } = useProgress();
  const done = doneCount(state);
  const pct = Math.round((done / 7) * 100);
  const next = resumeMission(state);
  return (
    <div className="grain relative overflow-hidden rounded-[24px] bg-bx p-6 text-white sm:p-8" data-testid="progress-block">
      <div className="relative z-10 grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Ma progression</p>
          <p className="mt-3 flex items-baseline gap-3">
            <span className="font-display text-5xl font-semibold" data-testid="progress-percent">{pct}%</span>
            <span className="text-white/80" data-testid="progress-count">{done} mission{done > 1 ? "s" : ""} terminée{done > 1 ? "s" : ""} sur 7</span>
          </p>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-white/15">
            <div className="h-full rounded-full bg-gold transition-[width] duration-700" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <BtnLink to={done === 7 ? "/resultats" : `/mission/${next}`} variant="light" data-testid="resume-journey-button">
          {done === 7 ? "Voir mes résultats" : done === 0 && missionStatus(state, 1) === "todo" ? "Commencer mon parcours" : "Reprendre mon parcours"}
          <ArrowRight className="h-4 w-4" />
        </BtnLink>
      </div>
    </div>
  );
}

function MissionCard({ m }) {
  const { state } = useProgress();
  const st = missionStatus(state, m.id);
  return (
    <Link to={`/mission/${m.id}`} data-testid={`mission-card-${m.id}`} className="group flex flex-col rounded-[18px] border border-line bg-white p-5 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:border-bx/20 hover:shadow-[0_16px_36px_-20px_rgba(101,31,56,0.35)] sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <span className="font-display text-3xl font-medium text-gold">{m.num}</span>
        <StatusBadge status={st} testId={`mission-card-${m.id}-status`} />
      </div>
      <m.icon className="mt-5 h-6 w-6 text-bx" strokeWidth={1.4} />
      <h3 className="mt-3 text-lg font-medium text-ink">{m.title}</h3>
      <p className="mt-1.5 flex-1 text-sm leading-relaxed text-soft">{m.short}</p>
      <span className={`mt-5 inline-flex items-center gap-1.5 text-sm font-semibold ${st === "done" ? "text-bx-2" : "text-bx"}`} data-testid={`mission-card-${m.id}-cta`}>
        {st === "todo" ? "Commencer" : st === "progress" ? "Continuer" : "Revoir"}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </Link>
  );
}

function Creation({ Icon, label, value, empty, to, testId }) {
  return (
    <div className="flex gap-4 border-b border-line py-4 last:border-0" data-testid={testId}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-bx/[0.06] text-bx"><Icon className="h-4 w-4" strokeWidth={1.6} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-soft">{label}</p>
        {value ? <p className="mt-1 whitespace-pre-line break-words text-ink">{value}</p> : <Link to={to} className="mt-1 inline-block text-sm text-bx-2 underline-offset-4 hover:underline">{empty}</Link>}
      </div>
    </div>
  );
}

function Creations() {
  const { state } = useProgress();
  const script = assembleScript(state.m4);
  const angle = ANGLES.find((a) => a.id === state.m3.angle);
  const concept = filled(state.m3.idea) ? `${angle ? angle.label + " — " : ""}${state.m3.idea}` : angle ? `Angle : ${angle.label}` : "";
  const shoot = filmedCount(state) + prepCount(state) + stepsCount(state) > 0 ? `${prepCount(state)}/6 préparatifs · ${filmedCount(state)}/4 plans filmés · ${stepsCount(state)}/9 étapes de réalisation` : "";
  return (
    <Card data-testid="my-creations-block">
      <h2 className="text-2xl font-medium text-bx">Mes créations</h2>
      <p className="mt-1 text-sm text-soft">Ce que tu as déjà construit pendant le parcours.</p>
      <div className="mt-4">
        <Creation Icon={Package} label="Produit" value={state.m2.name} empty="À choisir dans la mission 02" to="/mission/2" testId="creation-product" />
        <Creation Icon={Lightbulb} label="Concept" value={concept} empty="À définir dans la mission 03" to="/mission/3" testId="creation-concept" />
        <Creation Icon={PenLine} label="Script" value={script && (script.length > 180 ? script.slice(0, 180) + "…" : script)} empty="À écrire dans la mission 04" to="/mission/4" testId="creation-script" />
        <Creation Icon={Clapperboard} label="Tournage" value={shoot} empty="À préparer dans les missions 05 et 06" to="/mission/5" testId="creation-shoot" />
      </div>
    </Card>
  );
}

export default function Dashboard() {
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow="Ma première vidéo UGC" title="Ton espace créatif" subtitle="Chaque mission te rapproche de ta première vidéo." />
      <div className="space-y-10">
        <ProgressBlock />
        <section>
          <h2 className="mb-5 text-2xl font-medium text-bx">Mes missions</h2>
          <div className="stagger grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {MISSIONS.map((m) => <MissionCard key={m.id} m={m} />)}
          </div>
        </section>
        <Creations />
        <SaveNotice />
      </div>
    </div>
  );
}
